// Libs
import { Grid } from '@mui/material';
import { RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';

// Components
import { DriverProjectFields } from '../../../../../../../../domain/driversProject/DriversProject';
import { StopTask } from '../../../../../../../../domain/stop/StopTask';
import { RouteStopTaskField } from '../../../../../../../../domain/stop/StopTaskField';
import { Text } from '../../../../../../../../react-components/Text';

// Assets
import CheckIcon from '../../../../../../../../assets/img/icon-check.svg';

const style = {
  labelStyle: {
    fontSize: `13px`,
    color: RoutalPalette.grey7,
  },
  valueStyle: {
    fontSize: `13px`,
    color: RoutalPalette.text,
  },
  emptyValueStyle: {
    fontSize: `13px`,
    color: RoutalPalette.grey1,
  },
};

interface TaskFieldProps {
  field: RouteStopTaskField;
}

const TaskField = ({ field }: TaskFieldProps) => {
  return (
    <>
      {field.type === `multiselect` ? (
        <Grid>
          <VSpacer small />
          <Grid>
            {field.is_custom_field ? (
              <Text style={style.labelStyle}>{field.label}</Text>
            ) : (
              <Text style={style.labelStyle} intlId={`driver.stop.report.tasks.${field.id}`} />
            )}
          </Grid>
          <VSpacer small />
          <Grid style={{ display: `flex`, flexDirection: `row` }}>
            {field.value && Array.isArray(field.value) ? (
              field.value.map((value, index) => (
                <Grid key={field.id + field.is_custom_field + index} style={{ paddingRight: `10px` }}>
                  <Text
                    style={{
                      ...style.valueStyle,
                      padding: `2px 10px`,
                      borderRadius: `8px`,
                      border: `1px solid ${RoutalPalette.grey4}`,
                      boxShadow: `rgba(149, 157, 165, 0.2) 0px 8px 24px`,
                      backgroundColor: RoutalPalette.grey2,
                    }}
                  >
                    {value}
                  </Text>
                </Grid>
              ))
            ) : (
              <Text style={style.emptyValueStyle} intlId="driver.stop.entry.empty" />
            )}
          </Grid>
          <VSpacer small />
        </Grid>
      ) : field.type === `boolean` ? (
        <Grid>
          <VSpacer small />
          <Grid>
            {field.is_custom_field ? (
              <Text style={style.labelStyle}>{field.label}</Text>
            ) : (
              <Text style={style.labelStyle} intlId={`driver.stop.report.tasks.${field.id}`} />
            )}
          </Grid>
          <VSpacer small />
          <Grid
            style={{
              border: `2px solid ${RoutalPalette.grey5}`,
              aspectRatio: `1`,
              width: `20px`,
              height: `20px`,
              display: `flex`,
              justifyContent: `center`,
              alignItems: `center`,
              borderRadius: `3px`,
            }}
          >
            {field.value ? (
              <img src={CheckIcon} alt={`Checked boolean`} style={{ width: `18px`, height: `18px` }} />
            ) : null}
          </Grid>
          <VSpacer small />
        </Grid>
      ) : (
        <Grid>
          <VSpacer small />
          <Grid>
            {field.is_custom_field ? (
              <Text style={style.labelStyle}>{field.label}</Text>
            ) : (
              <Text style={style.labelStyle} intlId={`driver.stop.report.tasks.${field.id}`} />
            )}
          </Grid>
          <VSpacer small />
          <Grid>
            {field.value ? (
              <Text style={style.valueStyle}>{field.value}</Text>
            ) : (
              <Text style={style.emptyValueStyle} intlId="driver.stop.entry.empty" />
            )}
          </Grid>
          <VSpacer small />
        </Grid>
      )}
    </>
  );
};

interface TaskContentProps {
  task: StopTask;
  fields: DriverProjectFields;
}

export const TaskContent = ({ task, fields }: TaskContentProps) => {
  const taskFields = fields.map((field): RouteStopTaskField => {
    const taskField = task.fields.find((taskField) => taskField.id === field.id);

    if (!taskField || taskField.value === null) {
      return RouteStopTaskField.create({
        id: field.id,
        label: field.label,
        type: field.type,
        value: null,
        is_custom_field: field.is_custom_field,
      });
    }

    const routeStopTaskField = RouteStopTaskField.create({
      id: taskField.id,
      label: taskField.label,
      type: taskField.type,
      value: taskField.value,
      is_custom_field: taskField.is_custom_field,
    });

    if (routeStopTaskField.type === `select` || routeStopTaskField.type === `multiselect`) {
      const customFieldDefinition = fields.find(
        (customField) => customField.is_custom_field && customField.id === routeStopTaskField.id
      );

      if (!customFieldDefinition || !customFieldDefinition.options) {
        routeStopTaskField.value = null;
        return routeStopTaskField;
      }

      if (routeStopTaskField.type === `select`) {
        const index = (routeStopTaskField.value as number[])[0];

        routeStopTaskField.value = customFieldDefinition.options[index];

        return routeStopTaskField;
      }

      const indexes = routeStopTaskField.value as number[];

      routeStopTaskField.value = indexes.map((selection: number) => customFieldDefinition.options![selection]);
    }

    return routeStopTaskField;
  });

  return (
    <Grid>
      <VSpacer small />
      {taskFields.map((field) => (
        <TaskField key={field.id + field.is_custom_field} field={field} />
      ))}
    </Grid>
  );
};
